import { pauseWindowErrorMessage } from "@redotech/admin-sdk/coverage/pause-window-error-message";
import type { TimeOfDay } from "@redotech/arbiter/time/redo-time-picker";
import {
  dateToPlainDate,
  dateToTimeOfDay,
  plainDateToDate,
} from "@redotech/arbiter/time/time-zone-conversion";
import {
  MAX_MERCHANT_FEATURE_PAUSE_MS,
  validatePauseWindow,
} from "@redotech/redo-merchant-pause-common/paused-feature";

export interface CoveragePauseDraft {
  startDate: Temporal.PlainDate;
  startTime: TimeOfDay | null;
  startImmediately: boolean;
  endDate: Temporal.PlainDate;
  endTime: TimeOfDay | null;
}

export interface CoveragePauseRequest {
  /** Undefined when the pause starts right away, so the server stamps `now`. */
  startsAt: Date | undefined;
  endsAt: Date;
}

export type CoveragePauseWindow =
  | { request: CoveragePauseRequest; error: null }
  | { request: null; error: string };

export function defaultCoveragePauseDraft(
  now: Date,
  timeZone: string,
): CoveragePauseDraft {
  const start = unambiguousDateTime(
    new Date(now.getTime() + 15 * 60_000),
    timeZone,
    1,
  );
  // Leave an elapsed hour of headroom for a client clock ahead of the server.
  const end = unambiguousDateTime(
    new Date(now.getTime() + MAX_MERCHANT_FEATURE_PAUSE_MS - 60 * 60_000),
    timeZone,
    -1,
  );
  return {
    startDate: start.date,
    startTime: start.time,
    endDate: end.date,
    endTime: end.time,
    startImmediately: true,
  };
}

function unambiguousDateTime(
  instant: Date,
  timeZone: string,
  direction: 1 | -1,
) {
  for (let timestamp = instant.getTime(); ; timestamp += direction * 60_000) {
    const candidate = new Date(timestamp);
    const date = dateToPlainDate(candidate, timeZone);
    const time = dateToTimeOfDay(candidate, timeZone);
    if (isUnambiguousTime(date, time, timeZone)) {
      return { date, time };
    }
  }
}

/** Calendar date containing the elapsed-time limit, not an end-of-day bound. */
export function latestPauseEndDate(
  startsAt: Date,
  timeZone: string,
): Temporal.PlainDate {
  return dateToPlainDate(
    new Date(startsAt.getTime() + MAX_MERCHANT_FEATURE_PAUSE_MS),
    timeZone,
  );
}

function isUnambiguousTime(
  date: Temporal.PlainDate,
  time: TimeOfDay,
  timeZone: string,
): boolean {
  try {
    date
      .toPlainDateTime({ hour: time.hours, minute: time.minutes })
      .toZonedDateTime(timeZone, { disambiguation: "reject" });
    return true;
  } catch (error) {
    if (error instanceof RangeError) {
      return false;
    }
    throw error;
  }
}

/**
 * Turns the date/time picks from the pause form into the instants the RPC
 * takes, in the admin's time zone, and reports why they are not allowed.
 */
export function coveragePauseWindow(
  draft: CoveragePauseDraft,
  now: Date,
  timeZone: string = Temporal.Now.timeZoneId(),
): CoveragePauseWindow {
  let startsAt: Date | undefined;
  if (!draft.startImmediately) {
    if (!draft.startTime) {
      return { request: null, error: "Select a start time." };
    }
    if (!isUnambiguousTime(draft.startDate, draft.startTime, timeZone)) {
      return {
        request: null,
        error:
          "This start time is skipped or repeated by a daylight saving change. Select a different time.",
      };
    }
    startsAt = plainDateToDate(draft.startDate, draft.startTime, timeZone);
  }
  if (!draft.endTime) {
    return { request: null, error: "Select an end time." };
  }
  if (!isUnambiguousTime(draft.endDate, draft.endTime, timeZone)) {
    return {
      request: null,
      error:
        "This end time is skipped or repeated by a daylight saving change. Select a different time.",
    };
  }
  const endsAt = plainDateToDate(draft.endDate, draft.endTime, timeZone);
  const windowError = validatePauseWindow(
    { startsAt: startsAt ?? now, endsAt },
    now,
  );
  if (windowError) {
    return { request: null, error: pauseWindowErrorMessage(windowError) };
  }
  return { request: { startsAt, endsAt }, error: null };
}
